// CartLineItem — Apple-bag-style cart row: lead-image thumbnail, name,
// details line (type · unit price), right column (line total over the qty
// stepper + Remove). Inline styles only — no Tailwind. Themed via
// useEmbedTokens().
//
// TD-329: the qty stepper and the "Remove" action use the shared
// EmbedQtyStepper and EmbedLink primitives. The stepper is a joined tray
// (−/value/+) sharing one bordered pill; EmbedLink renders the error-coloured
// "Remove" as a borderless text action.
//
// Operator directive 2026-08-16 ("show product with icon and details and unit
// price etc, like real cart"): the row gained the thumbnail + details line +
// right column. This supersedes TD-224's "no product image" premise — TD-270
// later put `images[]` on the catalog wire, and the thumbnail renders only
// when the caller supplies catalogInfo (CartView resolves it from the public
// catalog endpoint; no image data → no <img>, never a broken box).

import { useEmbedTokens, EmbedQtyStepper, EmbedLink } from "../../embed-shared/primitives";
import { formatCurrency } from "../../embed-shared/currency";
import { formatLineRecurring } from "./product-display";
import { useStorefrontConfig } from "./storefront-config";
import type { CartLine } from "../../embed-shared/types";

/** Catalog-derived presentation data for a line. The AUTHENTICATED cart wire
 *  carries money but NOT the name/slug/image/type (Go model.CartLine has no
 *  such fields — captured live 2026-08-16); the GUEST projection carries
 *  empty strings. CartView resolves all four from the public catalog endpoint
 *  and passes them here. */
export interface CartLineCatalogInfo {
  /** Absolute lead-image URL. Server-side placeholder substitution (TD-324)
   *  means products without their own media still get the operator's
   *  placeholder here — a row is never broken for lack of an image. */
  image?: string;
  /** Canonical display label for the product type, e.g. "Hardware". */
  typeLabel?: string;
  /** Product name, for authenticated lines whose wire omits product_name. */
  name?: string;
  /** Product slug (PDP link), for authenticated lines whose wire omits
   *  product_slug. */
  slug?: string;
  /**
   * The product carries a DEVICE-basis price cell (device-count unit, Task
   * 10, ruling 1): quantity IS the device count on such a line, so the row
   * labels the count "devices" and the qty stepper says "Devices" — editing
   * it adds/removes devices (ruling 4). Resolved from the same public
   * catalog lookup as the other fields; ABSENT means "unknown" (lookup not
   * yet resolved / product outside the fetched page) and degrades to the
   * plain quantity wording.
   */
  devicePriced?: boolean;
}

export interface CartLineItemProps {
  line: CartLine;
  onUpdateQty: (lineId: string, quantity: number) => void;
  onRemove: (lineId: string) => void;
  /**
   * Name to show in place of `line.product_name`. Supplied for guest lines,
   * whose CartLine is projected from the staging buffer and therefore carries
   * an EMPTY product_name by construction — the buffer holds only
   * {product_id, quantity} (spec §6.1). CartView resolves the real name from
   * the public catalog and passes it here.
   */
  displayName?: string;
  /**
   * Whether to render money (line total + per-unit "each"). False for guest
   * lines: every money field on a guest line is 0 (SET_GUEST_LINES projects
   * through EMPTY_CART), so rendered prices would be fabricated, which spec
   * §6.2 forbids. Defaults to true — the authenticated cart is server-priced.
   */
  showLineTotal?: boolean;
  /** Catalog-resolved presentation data (thumbnail + type label). */
  catalogInfo?: CartLineCatalogInfo;
  /** Opens the product's detail page. Absent ⇒ name/thumbnail are inert. */
  onOpenProduct?: (slug: string) => void;
}

/**
 * Shown when no name is available at all. Reachable on the authenticated path
 * too: addLine's optimistic placeholder line also carries product_name: ""
 * (cart-context.tsx) until the server response replaces it, so without this
 * fallback that frame renders an empty <h4> and buttons announced as
 * "Remove  from cart" — an accessible name with a hole in it.
 */
const UNNAMED_LINE_LABEL = "Item";

/** Apple-bag thumbnail box: fixed square, token-rounded, hairline border. */
const THUMB_SIZE = 88;

export function CartLineItem({
  line,
  onUpdateQty,
  onRemove,
  displayName,
  showLineTotal = true,
  catalogInfo,
  onOpenProduct,
}: CartLineItemProps) {
  const t = useEmbedTokens();
  // ITEM A ruling (fix wave 2026-09-15), completed by TD-521 (2026-09-19):
  // currency is marketplace-wide, never per-product or per-line — EVERY money
  // format in this component threads this settings value (the line.currency
  // reads the ITEM A wave left behind were never populated on the wire).
  const { currency } = useStorefrontConfig();
  // Operator report (f), 2026-08-16: the authenticated wire OMITS product_name
  // and product_slug entirely (optional on CartLine) — every hop here must
  // tolerate undefined. Priority: guest displayName (with its positional
  // fallback) → server product_name (guest projection / future wire) → the
  // catalog lookup's name. An undefined at every hop renders the neutral
  // label instead of crashing the embed into its ErrorBoundary.
  const name =
    (displayName ?? line.product_name ?? catalogInfo?.name ?? "").trim() || UNNAMED_LINE_LABEL;
  // Slug: wire first (guest projection), catalog lookup for auth lines.
  const slug = (line.product_slug ?? catalogInfo?.slug ?? "").trim();
  const canOpen = Boolean(onOpenProduct && slug);

  const thumbBoxStyle = {
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: t.borderRadius,
    border: `1px solid ${t.colors.cardBorder}`,
    backgroundColor: t.colors.cardBg,
    overflow: "hidden",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: canOpen ? "pointer" : "default",
  } as const;

  const thumbnail = catalogInfo?.image ? (
    <img
      src={catalogInfo.image}
      // Decorative (H6 pattern): the name carries the meaning; the image must
      // not duplicate it to screen readers.
      alt=""
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
    />
  ) : null;

  // The thumbnail: a link to the PDP when navigation is available (Apple-bag
  // rows link to the product), otherwise an inert box. type="button" (§73).
  const thumb = canOpen ? (
    <button
      type="button"
      aria-label={`View ${name}`}
      onClick={() => onOpenProduct?.(slug)}
      style={thumbBoxStyle}
    >
      {thumbnail}
    </button>
  ) : (
    <div style={thumbBoxStyle}>{thumbnail}</div>
  );

  // Details line under the name: "Hardware · $10.00 each". The per-unit
  // "each" only reads as information when qty > 1 — at qty 1 the line total
  // IS the unit price, and "$59.00 each" beside "$59.00" is noise. Money is
  // gated on showLineTotal (guest rows must show none — spec §6.2).
  // C2 Task 2 (CART-8, TD-518): the "each" figure is only true on a "per_unit"
  // line. On a "whole_line" (device-priced) line unit_price IS the whole-line
  // price (store x1 + device x count), so "$275 each" beside a $275 line total
  // would contradict itself — render no per-unit figure there. An ABSENT
  // price_basis (older API during a deploy window) keeps the old behaviour so
  // nothing regresses before the backend ships.
  const details: string[] = [];
  if (catalogInfo?.typeLabel) details.push(catalogInfo.typeLabel);
  if (showLineTotal && line.quantity > 1 && line.price_basis !== "whole_line") {
    details.push(`${formatCurrency(line.unit_price, currency)} each`);
  }

  // Subscription pricing detail (device-count unit, Task 10, ruling 4):
  // recurring_amount is the WHOLE-LINE per-period amount (already folded by
  // quantity for a device line — Task 7), so it renders as the line's
  // per-period subscription price in the same wording the catalog uses
  // (formatLineRecurring reuses intervalWord's mapping). On a device-priced
  // line the count is named — quantity IS the device count (ruling 1).
  // Fail-closed: an unknown interval omits the row (never a raw slug); the
  // guest rows show no money at all (spec §6.2).
  const isDeviceLine = catalogInfo?.devicePriced === true;
  const subscriptionText = showLineTotal
    ? formatLineRecurring(line.recurring_amount, line.recurring_interval, currency)
    : null;
  const subscriptionRow =
    subscriptionText != null
      ? isDeviceLine && line.quantity > 1
        ? `${subscriptionText} · ${line.quantity} devices`
        : subscriptionText
      : null;

  return (
    <div
      style={{
        display: "flex",
        gap: t.spacing.lg,
        padding: `${t.spacing.lg}px 0`,
        borderBottom: `1px solid ${t.colors.cardBorder}`,
        alignItems: "flex-start",
      }}
    >
      {thumb}

      {/* Details column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* The name stays a heading (a11y contract, CartLineItem tests): when
            navigation is available its CONTENT is a button; otherwise plain
            text. Button is fully style-reset so the heading typography owns
            the look (Apple: the product name reads as a quiet link). */}
        <h4
          style={{
            fontSize: t.fontSize.sm,
            fontWeight: 600,
            color: t.colors.text,
            margin: 0,
            lineHeight: t.lineHeight.tight,
          }}
        >
          {canOpen ? (
            <button
              type="button"
              onClick={() => onOpenProduct?.(slug)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                color: "inherit",
                textAlign: "left",
                cursor: "pointer",
                textDecoration: "underline",
                textDecorationColor: t.colors.cardBorder,
                textUnderlineOffset: 2,
              }}
            >
              {name}
            </button>
          ) : (
            name
          )}
        </h4>

        {details.length > 0 && (
          <p
            style={{
              margin: 0,
              marginTop: t.spacing.xs,
              fontSize: t.fontSize.sm,
              color: t.colors.textSecondary,
            }}
          >
            {details.join(" · ")}
          </p>
        )}
      </div>

      {/* Right column: line total above the qty stepper + Remove (Apple-bag
          placement) — money gated on showLineTotal as above. */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: t.spacing.sm,
          flexShrink: 0,
        }}
      >
        {showLineTotal && (
          <span
            style={{
              fontSize: t.fontSize.sm,
              fontWeight: 600,
              color: t.colors.text,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {formatCurrency(line.line_total, currency)}
          </span>
        )}
        {subscriptionRow && (
          <span
            style={{
              fontSize: t.fontSize.sm,
              color: t.colors.textSecondary,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {subscriptionRow}
          </span>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: t.spacing.sm }}>
          <EmbedQtyStepper
            ariaLabel={`${isDeviceLine ? "Devices" : "Quantity"} for ${name}`}
            value={line.quantity}
            min={1}
            onDecrease={() => onUpdateQty(line.id, line.quantity - 1)}
            onIncrease={() => onUpdateQty(line.id, line.quantity + 1)}
          />

          <EmbedLink
            variant="danger"
            aria-label={`Remove ${name} from cart`}
            onClick={() => onRemove(line.id)}
          >
            Remove
          </EmbedLink>
        </div>
      </div>
    </div>
  );
}
